import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import api from "@/lib/api";
import { useTheme } from "next-themes";
import { F, th as palette, Btn } from "@/lib/bricks";
import { Page, PageHeader, Stats, StatusTabs, List, Row, Empty, TextLink, Loading, FieldLabel } from "@/components/calm/Calm";
import { toast } from "sonner";

const TABS = ['All', 'Active', 'At limit', 'Dormant'];

export default function TeamMembersPage() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('All');
  const [editMember, setEditMember] = useState(null);
  const [allocation, setAllocation] = useState("");
  const [inviteModal, setInviteModal] = useState(false);
  const [inviteData, setInviteData] = useState({ name: '', email: '', allocation: 10 });
  const [inviting, setInviting] = useState(false);
  const { resolvedTheme } = useTheme();
  const dark = resolvedTheme === 'dark';
  const p = palette(dark);

  const load = () => {
    api.get("/agents")
      .then(r => { setMembers(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const totalUsed = members.reduce((s, m) => s + (m.shoots_used || 0), 0);
  const totalAlloc = members.reduce((s, m) => s + (m.monthly_allocation || 0), 0);
  const atCap = members.filter(m => (m.monthly_allocation > 0) && (m.shoots_used / m.monthly_allocation) >= 0.9).length;

  const filtered = members.filter(m => {
    const pct = m.monthly_allocation > 0 ? (m.shoots_used / m.monthly_allocation) * 100 : 0;
    if (activeTab === 'All') return true;
    if (activeTab === 'Active') return m.shoots_used > 0;
    if (activeTab === 'At limit') return pct >= 90;
    if (activeTab === 'Dormant') return !m.shoots_used || m.shoots_used === 0;
    return true;
  });

  const handleUpdateAllocation = async () => {
    if (!editMember || !allocation) return;
    try {
      await api.post(`/agents/${editMember.user_id}/allocation`, { monthly_allocation: parseInt(allocation) });
      toast.success("Allocation updated");
      setEditMember(null);
      load();
    } catch { toast.error("Failed to update allocation"); }
  };

  const handleInvite = async () => {
    if (!inviteData.name?.trim() || !inviteData.email?.trim()) {
      return toast.error("Name and email are required");
    }
    setInviting(true);
    try {
      await api.post("/agents/invite", {
        name: inviteData.name.trim(),
        email: inviteData.email.trim(),
        allocation: inviteData.allocation || 10,
      });
      toast.success(`Invite sent to ${inviteData.email.trim()}`);
      setInviteModal(false);
      setInviteData({ name: '', email: '', allocation: 10 });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invite");
    } finally {
      setInviting(false);
    }
  };

  if (loading) return <Loading />;

  const rowLink = { background:'none', border:'none', padding:0, cursor:'pointer', fontFamily:F.display, fontSize:13, color:p.ink2, textDecoration:'underline', textUnderlineOffset:3, textDecorationColor:p.hairS, whiteSpace:'nowrap' };

  return (
    <Page testid="team-members-page" width={960}>
      <PageHeader title="Team members" help="teams" helpLabel="Help for teams"
        subtitle={members.length ? `${members.length} member${members.length === 1 ? '' : 's'} sharing ${totalAlloc} shoots a month.` : 'Invite the agents on your team.'}
        action={<Btn variant="primary" dark={dark} onClick={() => setInviteModal(true)}>Invite member</Btn>} />

      {members.length > 0 && (
        <Stats items={[
          { label:'Members', value:String(members.length) },
          { label:'Shoots used', value:`${totalUsed}/${totalAlloc}` },
          { label:'Near their limit', value:String(atCap), attention: atCap > 0 },
        ]} />
      )}

      <StatusTabs label="Members" options={TABS.map(t => ({ key:t, label:t }))} value={activeTab} onChange={setActiveTab} testid="team-members-tabs" />

      {filtered.length === 0 ? (
        <Empty action={members.length ? null : <TextLink onClick={() => setInviteModal(true)}>Invite your first member</TextLink>}>
          {members.length ? (activeTab === 'At limit' ? 'Nobody is near their limit.' : activeTab === 'Dormant' ? 'Everyone has booked this month.' : 'No members here.') : 'No team members yet.'}
        </Empty>
      ) : (
        <List testid="team-members-list">
          {filtered.map(m => {
            const pct = m.monthly_allocation > 0 ? (m.shoots_used / m.monthly_allocation) * 100 : 0;
            const used = m.shoots_used || 0;
            return (
              <Row key={m.user_id || m.agent_id} testid={`member-${m.user_id || m.agent_id}`}
                title={m.name || 'Unnamed member'}
                meta={[m.email]}
                next={[{ text:`${used} of ${m.monthly_allocation || 0} shoots this month${pct >= 90 ? ', near their limit' : ''}`, attention: pct >= 90 }]}
                status={used === 0 ? 'Dormant' : 'Active'}
                aside={<button style={rowLink} onClick={() => { setEditMember(m); setAllocation(String(m.monthly_allocation || '')); }}>Set allocation</button>} />
            );
          })}
        </List>
      )}

      {/* Allocation dialog */}
      <Dialog open={!!editMember} onOpenChange={open => { if (!open) setEditMember(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set allocation for {editMember?.name}</DialogTitle>
          </DialogHeader>
          <div style={{ padding:'16px 0' }}>
            <label style={{ fontSize:13, color:p.ink2, display:'block', marginBottom:8 }}>Monthly shoot allocation</label>
            <input
              type="number" min={0} value={allocation}
              onChange={e => setAllocation(e.target.value)}
              style={{ width:'100%', padding:'10px 12px', border:`1px solid ${p.hair}`, borderRadius:2, fontFamily:F.mono, fontSize:14, background:p.bg, color:p.ink }}
              placeholder="e.g. 2"
            />
          </div>
          <DialogFooter>
            <Btn variant="ghost" dark={dark} onClick={() => setEditMember(null)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} onClick={handleUpdateAllocation}>Save</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite member modal — same /agents/invite stack as AgentsPage */}
      <Dialog open={inviteModal} onOpenChange={open => { if (!inviting) setInviteModal(open); }}>
        <DialogContent style={{ maxWidth: 400, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, letterSpacing: -0.3, color: p.ink }}>Invite member</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>
              Invite someone to your team. They will get an email to join.
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20, paddingTop: 20 }}>
            <div>
              <FieldLabel>Full name</FieldLabel>
              <input
                type="text" value={inviteData.name}
                onChange={e => setInviteData({ ...inviteData, name: e.target.value })}
                placeholder="e.g. Marisol Ortega"
                disabled={inviting}
                style={{ marginTop: 10, width: '100%', padding: '12px 14px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <FieldLabel>Email address</FieldLabel>
              <input
                type="email" value={inviteData.email}
                onChange={e => setInviteData({ ...inviteData, email: e.target.value })}
                placeholder="marisol@example.com"
                disabled={inviting}
                style={{ marginTop: 10, width: '100%', padding: '12px 14px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <FieldLabel>Monthly allocation</FieldLabel>
              <input
                type="number" min={0} value={inviteData.allocation}
                onChange={e => setInviteData({ ...inviteData, allocation: parseInt(e.target.value) || 0 })}
                disabled={inviting}
                style={{ marginTop: 10, width: '100%', padding: '12px 14px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
          </div>
          <DialogFooter style={{ marginTop: 32, gap: 10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => !inviting && setInviteModal(false)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} onClick={handleInvite} disabled={inviting}>{inviting ? 'Sending…' : 'Send invite'}</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
